// src/pages/Home.js
import React, { useState, useEffect } from 'react';
import { Leaf, Truck, RotateCcw, ShieldCheck, Search, SlidersHorizontal } from 'lucide-react';
import { collection, getDocs, query, orderBy, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import ProductCard from '../components/ProductCard';
import CategoryCard from '../components/CategoryCard';
import HeroSlider from '../components/HeroSlider';
import TimerBanner from '../components/TimerBanner';
import ImageGallery from '../components/ImageGallery';
import VideoGallery from '../components/VideoGallery';
import MarqueeStrip from '../components/MarqueeStrip';
import ReviewSlider from '../components/ReviewSlider';
import CouponSlider from '../components/CouponSlider';
import WhyChooseUs from '../components/WhyChooseUs';
import AppDownloadSection from '../components/AppDownloadSection';

const Home = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all');
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sectionTitles, setSectionTitles] = useState({
    categoryTitle: 'Shop by Category',
    categorySubtitle: 'Browse our wide range of organic products',
    featuredTitle: 'Featured Products',
    featuredSubtitle: 'Hand-picked organic products for you',
    allProductsTitle: 'All Products',
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // 1. Fetch Categories
        const categoriesSnap = await getDocs(collection(db, 'categories'));
        const categoriesData = categoriesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

        // 2. Fetch Products
        const productsQuery = query(collection(db, 'products'), orderBy('createdAt', 'desc'));
        const productsSnap = await getDocs(productsQuery);
        const productsData = productsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setProducts(productsData);

        // ✅ 3. Update Categories with Real Product Count
        const updatedCategories = categoriesData.map(cat => {
          const count = productsData.filter(p => p.category === cat.name).length;
          return { ...cat, productCount: count };
        });
        setCategories(updatedCategories);

        // 4. Fetch Section Titles
        try {
          const settingsRef = doc(db, 'settings', 'site');
          const settingsSnap = await getDoc(settingsRef);
          if (settingsSnap.exists()) {
            const data = settingsSnap.data();
            setSectionTitles({
              categoryTitle: data.categoryTitle || 'Shop by Category',
              categorySubtitle: data.categorySubtitle || 'Browse our wide range of organic products',
              featuredTitle: data.featuredTitle || 'Featured Products',
              featuredSubtitle: data.featuredSubtitle || 'Hand-picked organic products for you',
              allProductsTitle: data.allProductsTitle || 'All Products',
            });
          }
        } catch (err) {
          console.error('Error loading settings:', err);
        }

      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const getFilteredProducts = () => {
    let filtered = [...products];
    if (searchTerm) {
      filtered = filtered.filter(p =>
        p.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.category?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    if (filter === 'low-high') filtered.sort((a, b) => (a.price || 0) - (b.price || 0));
    else if (filter === 'high-low') filtered.sort((b, a) => (a.price || 0) - (b.price || 0));
    else if (filter === 'popular') filtered.sort((a, b) => (b.reviews || 0) - (a.reviews || 0));
    return filtered;
  };

  const filteredProducts = getFilteredProducts();

  return (
    <div className="w-full" style={{ backgroundColor: 'var(--color-body-bg)', color: 'var(--color-primary-text)' }}>

      <HeroSlider />

      <MarqueeStrip />

      {/* SEARCH + FILTER */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 py-5 md:py-8">
        <div className="rounded-xl shadow-md p-3 md:p-4 flex flex-col md:flex-row gap-3 md:gap-4 items-center"
          style={{ backgroundColor: 'var(--color-card-bg)', border: `1px solid var(--color-border)` }}>
          <div className="relative flex-1 w-full">
            <input type="text" placeholder="Search products..." value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full rounded-lg py-3 px-4 pl-11 focus:outline-none text-sm md:text-base"
              style={{ border: `1px solid var(--color-border)`, color: 'var(--color-primary-text)', backgroundColor: 'var(--color-card-bg)' }} />
            <Search className="absolute left-3 top-3.5" size={18} style={{ color: 'var(--color-secondary-text)' }} />
          </div>
          <div className="flex items-center gap-2 w-full md:w-auto">
            <SlidersHorizontal className="shrink-0" size={18} style={{ color: 'var(--color-link)' }} />
            <select value={filter} onChange={(e) => setFilter(e.target.value)}
              className="rounded-lg py-3 px-3 focus:outline-none cursor-pointer w-full md:w-auto text-sm md:text-base"
              style={{ border: `1px solid var(--color-border)`, color: 'var(--color-primary-text)', backgroundColor: 'var(--color-card-bg)' }}>
              <option value="all">All Products</option>
              <option value="low-high">Price: Low to High</option>
              <option value="high-low">Price: High to Low</option>
              <option value="popular">Most Popular</option>
            </select>
          </div>
        </div>
      </section>

      {loading && (
        <div className="text-center py-20">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-t-transparent"
            style={{ borderColor: 'var(--color-link)', borderTopColor: 'transparent' }}></div>
          <p className="mt-4" style={{ color: 'var(--color-secondary-text)' }}>Loading products...</p>
        </div>
      )}

      {/* CATEGORIES */}
      {!loading && categories.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-10">
          <div className="text-center mb-6 md:mb-10">
            <h2 className="text-2xl md:text-4xl font-bold mb-2 anim-fadeInUp" style={{ color: 'var(--color-primary-text)' }}>
              {sectionTitles.categoryTitle}
            </h2>
            <p className="text-sm md:text-base anim-fadeInUp delay-100" style={{ color: 'var(--color-secondary-text)' }}>
              {sectionTitles.categorySubtitle}
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-6 md:gap-8">
            {categories.map((category, i) => (
              <div key={category.id} className="anim-fadeInUp" style={{ animationDelay: `${i * 80}ms` }}>
                <CategoryCard category={category} />
              </div>
            ))}
          </div>
        </section>
      )}

      {/* COUPON SLIDER */}
      {!loading && <CouponSlider />}

      {/* FEATURED */}
      {!loading && filteredProducts.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-10 rounded-2xl" style={{ backgroundColor: '#FFF2F6' }}>
          <div className="text-center mb-6 md:mb-10">
            <h2 className="text-2xl md:text-4xl font-bold mb-2 anim-fadeInUp" style={{ color: 'var(--color-primary-text)' }}>
              {sectionTitles.featuredTitle}
            </h2>
            <p className="text-sm md:text-base anim-fadeInUp delay-100" style={{ color: 'var(--color-secondary-text)' }}>
              {sectionTitles.featuredSubtitle}
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6">
            {filteredProducts.slice(0, 4).map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        </section>
      )}

      {/* ALL PRODUCTS */}
      {!loading && filteredProducts.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-10">
          <div className="text-center mb-6 md:mb-10">
            <h2 className="text-2xl md:text-4xl font-bold mb-2 anim-fadeInUp" style={{ color: 'var(--color-primary-text)' }}>
              {sectionTitles.allProductsTitle}
            </h2>
            <p className="text-sm md:text-base anim-fadeInUp delay-100" style={{ color: 'var(--color-secondary-text)' }}>
              {filteredProducts.length} products available
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6">
            {filteredProducts.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        </section>
      )}

      {/* TIMER BANNER */}
      {!loading && <TimerBanner />}

      {/* WHY CHOOSE US */}
      {!loading && <WhyChooseUs />}

      {/* PHOTO GALLERY */}
      {!loading && <ImageGallery />}

      {/* VIDEO GALLERY */}
      {!loading && <VideoGallery />}

      {/* APP DOWNLOAD SECTION */}
      {!loading && <AppDownloadSection />}

      {/* REVIEWS SLIDER */}
      {!loading && <ReviewSlider />}

      {!loading && filteredProducts.length === 0 && products.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 md:px-6 py-20 text-center">
          <p className="text-lg" style={{ color: 'var(--color-secondary-text)' }}>
            No products found for "{searchTerm}"
          </p>
        </section>
      )}

      {!loading && products.length === 0 && (
        <section className="max-w-7xl mx-auto px-4 md:px-6 py-20 text-center">
          <Leaf size={48} className="mx-auto mb-4" style={{ color: 'var(--color-link)' }} />
          <h3 className="text-xl font-bold mb-2" style={{ color: 'var(--color-primary-text)' }}>
            No Products Yet
          </h3>
          <p className="text-sm" style={{ color: 'var(--color-secondary-text)' }}>
            Admin panel se products add karein.
          </p>
        </section>
      )}

      {/* FEATURES SECTION - PINK */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-10 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 md:gap-6" style={{ backgroundColor: '#FFF2F6' }}>
        <div className="rounded-xl shadow-md p-5 md:p-6 text-center hover:shadow-lg transition anim-fadeInUp" style={{ backgroundColor: '#FFFFFF', border: `1px solid #F5AEC0` }}>
          <Truck className="mx-auto mb-3" size={36} style={{ color: '#DC829A' }} />
          <h3 className="text-lg md:text-xl font-bold mb-2" style={{ color: '#9B3F5B' }}>Free Shipping</h3>
          <p className="text-sm" style={{ color: '#DC829A' }}>On orders above Rs. 2000</p>
        </div>
        <div className="rounded-xl shadow-md p-5 md:p-6 text-center hover:shadow-lg transition anim-fadeInUp delay-100" style={{ backgroundColor: '#FFFFFF', border: `1px solid #F5AEC0` }}>
          <RotateCcw className="mx-auto mb-3" size={36} style={{ color: '#DC829A' }} />
          <h3 className="text-lg md:text-xl font-bold mb-2" style={{ color: '#9B3F5B' }}>7-Day Returns</h3>
          <p className="text-sm" style={{ color: '#DC829A' }}>Easy return & refund policy</p>
        </div>
        <div className="rounded-xl shadow-md p-5 md:p-6 text-center hover:shadow-lg transition sm:col-span-2 md:col-span-1 anim-fadeInUp delay-200" style={{ backgroundColor: '#FFFFFF', border: `1px solid #F5AEC0` }}>
          <ShieldCheck className="mx-auto mb-3" size={36} style={{ color: '#DC829A' }} />
          <h3 className="text-lg md:text-xl font-bold mb-2" style={{ color: '#9B3F5B' }}>100% Organic</h3>
          <p className="text-sm" style={{ color: '#DC829A' }}>Certified organic products</p>
        </div>
      </section>

      <div className="h-20 md:h-0"></div>
    </div>
  );
};

export default Home;